/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

import { UserSessionContext } from 'features/session/userSessionStore';
import { normalizeSupportedLanguage } from 'shared/utils/supportedLanguage';
import { LocalStorage } from 'shared/utils/storage';
import EVENT_NAMES from 'shared/constants/events';
import { getUrlParameter } from 'shared/utils/common';
import Emitter from 'shared/events/emitter';
import { cn } from 'lib/utils';

function UserSettings() {
  const { t } = useTranslation();
  const [{ user }, dispatch] = React.useContext(UserSessionContext);

  const [isOpen, setIsOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const open = () => setIsOpen(true);
    if (EVENT_NAMES.USER_SETTINGS?.OPEN) {
      Emitter.on(EVENT_NAMES.USER_SETTINGS.OPEN, open);
      return () => Emitter.off(EVENT_NAMES.USER_SETTINGS.OPEN, open);
    }
  }, []);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const userObj = user as Record<string, unknown> | null | undefined;
  const currentLanguage = normalizeSupportedLanguage(userObj?.language as string);
  const currentOddsFormat = (userObj?.odds_format as string) || 'decimal';

  React.useEffect(() => {
    if (userObj?.language) {
      const normalized = normalizeSupportedLanguage(userObj.language as string);
      if (i18n.language !== normalized) {
        i18n.changeLanguage(normalized);
      }
    }
  }, [userObj?.language]);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ];

  const oddsFormatOptions = [
    { code: 'decimal', name: 'Decimal (2.50)' },
    { code: 'fractional', name: 'Fractional (3/2)' },
    { code: 'american', name: 'American (+150)' },
  ];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    if (userObj) {
      const updatedUser = { ...userObj, language: languageCode.toUpperCase() };
      dispatch({ type: 'SET_USER', payload: updatedUser });
    }
  };

  const handleOddsFormatChange = (oddsFormat: string) => {
    if (userObj) {
      const updatedUser = { ...userObj, odds_format: oddsFormat };
      dispatch({ type: 'SET_USER', payload: updatedUser });
    }
  };

  const handleLogout = () => {
    Emitter.emit(EVENT_NAMES.AUTH.LOGOUT_PROCEED, {});
    setIsOpen(false);
  };

  const showLogout = (userObj && userObj.guest !== true) || getUrlParameter('s');

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hidden md:flex fixed top-4 right-4 z-[100] w-10 h-10 rounded-full bg-accent bg-background border border-accent border-border items-center justify-center shadow-md hover:bg-primary hover:border-primary hover:text-primary-foreground transition-all text-foreground"
        aria-label={t('USER_SETTINGS')}
      >
        <i className="bi bi-gear text-lg"></i>
      </button>

      {isOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[99] transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={cn(
          'fixed top-0 right-0 h-full bg-background border-l border-border shadow-lg z-[100] overflow-y-auto transition-transform duration-300 ease-out',
          'w-full md:w-[400px]',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          !isOpen && 'pointer-events-none'
        )}
      >
        {isOpen && (
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-border">
              <h5 className="text-lg font-semibold text-foreground">{t('USER_SETTINGS')}</h5>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t('CLOSE')}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-foreground">
                  <i className="bi bi-translate mr-2 text-primary"></i>
                  {t('LANGUAGE')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={cn(
                        'flex items-center px-3 py-2 border rounded-md text-sm transition-all',
                        currentLanguage === lang.code
                          ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground hover:border-primary/90'
                          : 'border-border bg-background text-foreground hover:border-primary hover:bg-muted'
                      )}
                      onClick={() => handleLanguageChange(lang.code)}
                      title={lang.name}
                    >
                      <span className="text-lg mr-2">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-foreground">
                  <i className="bi bi-percent mr-2 text-primary"></i>
                  {t('ODDS_FORMAT')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {oddsFormatOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      className={cn(
                        'flex items-center px-3 py-2 border rounded-md text-sm transition-all',
                        currentOddsFormat === option.code
                          ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground hover:border-primary/90'
                          : 'border-border bg-background text-foreground hover:border-primary hover:bg-muted'
                      )}
                      onClick={() => handleOddsFormatChange(option.code)}
                      title={option.name}
                    >
                      <span className="font-medium">{option.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {showLogout && (
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-foreground">
                    <i className="bi bi-box-arrow-right mr-2 text-primary"></i>
                    {t('LOGOUT')}
                  </label>
                  <button
                    type="button"
                    className="flex items-center px-3 py-2 border border-border rounded-md text-sm transition-all bg-background text-foreground hover:border-primary hover:bg-muted"
                    onClick={handleLogout}
                  >
                    <span className="font-medium">{t('LOGOUT')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default UserSettings;
